<!-- ArrowStepper.vue -->
<template>
  <div class="chevron-progress-stepper">
    <div class="stepper-container">
      <template v-for="(step, index) in steps">
        <!-- Tooltip wrapper per step -->
        <v-tooltip
          :key="step.id || index"
          top
          :open-delay="150"
          :close-delay="0"
          content-class="step-tooltip"
        >
          <template v-slot:activator="{ on }">
            <div
              class="step-item"
              :class="[
                getStepClass(step),
                { 'not-first': index > 0, 'is-first': index === 0, 'is-last': index === steps.length - 1 }
              ]"
              v-on="on"
              :aria-label="$t(step.label)"
            >
              <div class="step-content">
                <v-icon v-if="step.status === 'completed'" class="step-icon" size="18">check</v-icon>
                <span class="step-label">
                  {{ $t(step.label) }}
                  <!-- Mobile inline both dates -->
                  <span
                    v-if="mobileHasAnyDate(step)"
                    class="deadline-inline"
                  >
                    <template v-if="step.deadline">
                      • {{ $t('limit') }} {{ formatDeadline(step.deadline) }}
                    </template>
                    <template v-if="step.completed_date">
                      • {{ $t('completed') }} {{ formatDeadline(step.completed_date) }}
                    </template>
                  </span>
                </span>
              </div>
            </div>
          </template>
          <span class="tooltip-text">{{ $t(step.label) }}</span>
        </v-tooltip>

        <!-- Chevron between steps -->
        <div
          v-if="index < steps.length - 1"
          class="step-chevron"
          :key="'chevron-' + (step.id || index)"
        ></div>
      </template>
    </div>

    <!-- DESKTOP: show both limit & completed dates -->
    <div
      v-if="hasAnyTimelineInfo"
      class="deadline-row"
      :style="gridColsStyle"
    >
      <div
        v-for="(step, index) in steps"
        :key="'deadline-' + (step.id || index)"
        class="deadline-cell"
      >
        <template v-if="step.deadline || step.completed_date">
          <div v-if="step.deadline">
            <span class="deadline-label">{{ $t('limit') }}:</span>
            <span class="deadline-date">{{ formatDeadline(step.deadline) }}</span>
          </div>
          <div v-if="step.completed_date">
            <span class="deadline-label">{{ $t('completed') }}:</span>
            <span class="deadline-date">{{ formatDeadline(step.completed_date) }}</span>
          </div>
        </template>
        <template v-else>
          <span class="deadline-empty">&nbsp;</span>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ArrowStepper',
  props: {
    steps: { type: Array, required: true },
    // Optional custom date formatter (date: string|Date) => string
    deadlineFormatter: { type: Function, default: null }
  },
  computed: {
    gridColsStyle () {
      const n = (this.steps && this.steps.length) || 1
      return { gridTemplateColumns: `repeat(${n}, 1fr)` }
    },
    hasAnyTimelineInfo () {
      if (!this.steps) return false
      return this.steps.some(s => !!s.deadline || !!s.completed_date)
    }
  },
  methods: {
    /** detect "completed but late" */
    isLate (step) {
      if (!step || !step.completed_date || !step.deadline) return false
      const done = this.toDateOnly(step.completed_date)
      const limit = this.toDateOnly(step.deadline)
      if (!done || !limit) return false
      return done.getTime() > limit.getTime()
    },

    getStepClass (step) {
      const status = step && step.status
      return {
        completed: status === 'completed' && !this.isLate(step),
        late: status === 'completed' && this.isLate(step),
        current: status === 'current',
        pending: status === 'pending'
      }
    },

    formatDeadline (dateLike) {
      if (this.deadlineFormatter) return this.deadlineFormatter(dateLike)
      return this.displayDate(dateLike)
    },

    displayDate (iso) {
      if (!iso) return ''
      if (iso instanceof Date && !isNaN(iso)) {
        const yyyy = iso.getFullYear()
        const mm = String(iso.getMonth() + 1).padStart(2, '0')
        const dd = String(iso.getDate()).padStart(2, '0')
        iso = `${yyyy}-${mm}-${dd}`
      }
      const str = String(iso)
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str)
      if (!m) return str
      return `${m[3]}/${m[2]}/${m[1]}`
    },

    toDateOnly (val) {
      if (!val) return null
      if (val instanceof Date && !isNaN(val)) {
        return new Date(val.getFullYear(), val.getMonth(), val.getDate())
      }
      const s = String(val)
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
      if (!m) return null
      const y = +m[1], mo = +m[2] - 1, d = +m[3]
      return new Date(y, mo, d)
    },

    /* ------- Inline (mobile) helpers ------- */
    mobileHasAnyDate (step) {
      return !!(step.deadline || step.completed_date)
    }
  }
}
</script>

<style scoped>
.step-tooltip {
  background-color: #fff;
  color: #333 !important;
  opacity: 1 !important;
}

.step-tooltip .tooltip-text {
  color: #333 !important;
  font-weight: 600;
  font-size: 0.95rem;
  opacity: 1 !important;
}

.chevron-progress-stepper {
  width: 100%;
  padding: 1rem 0;
}

.stepper-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: none;
}

.step-item {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 2rem;
  height: 38px;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  clip-path: polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%);
  flex: 1 1 0;
  min-width: 0;
}

.step-item:first-child {
  padding-left: 2rem;
  clip-path: polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%);
}

.step-item:last-child {
  padding-right: 1.5rem;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%, 20px 50%);
}

.step-item:first-child:last-child {
  clip-path: none;
  border-radius: 8px;
}

/* existing */
.step-item.completed { background-color: #35a16a; z-index: 3; }
.step-item.current   { background-color: #ffdd61; color: #333; z-index: 2; }
.step-item.pending   { background-color: #d7d7d7; color: #333; z-index: 1; }

/* completed but late */
.step-item.late { background-color: #ff8c42; color: #fff; z-index: 3; }  /* orange */
.step-item.late:hover { background-color: #e57b37; }

/* hover states */
.step-item:hover { transform: translateY(-1px); }
.step-item.completed:hover { background-color: #2b8657; }
.step-item.current:hover   { background-color: #e3c555; }
.step-item.pending:hover   { background-color: #b4b4b4; }

.step-item.completed .step-label,
.step-item.completed .step-icon { color: #fff !important; }
.step-item.late .step-label,
.step-item.late .step-icon { color: #fff !important; }

.step-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.25rem;
  min-width: 0;
}

.step-icon { font-size: 1rem; font-weight: bold; }

.step-label {
  font-size: 1rem;
  font-weight: 600;
  min-width: 0;
}

.step-item.not-first { margin-left: -12px; }

.deadline-row {
  margin-top: 8px;
  display: grid;
  gap: 0;
}

.deadline-cell {
  text-align: center;
  padding-top: 4px;
  font-weight: 700;
  color: #5d5d5d;
  font-size: 0.95rem;
}

.deadline-label {
  opacity: 0.85;
  margin-right: 6px;
  font-weight: 200;
}

.deadline-date { font-weight: 700; }

.deadline-inline {
  display: none;
  font-weight: 600;
  margin-left: 6px;
  opacity: 0.95;
}

@media (max-width: 768px) {
  .step-item { font-size: 0.9rem; }
}

@media (min-width: 481px) and (max-width: 768px) {
  .step-item {
    height: 36px;
    padding-inline: 1.5rem;
  }
  .step-item:not(.is-last) {
    padding-right: 2.25rem;
  }
  .step-item.is-first {
    padding-left: 1.75rem;
  }
  .step-content { padding-inline: 0.25rem; }
}

@media (max-width: 480px) {
  .stepper-container { flex-direction: column; gap: 0.5rem; }
  .step-item {
    width: 100%;
    justify-content: center;
    clip-path: none;
    border-radius: 8px;
    padding: 0.75rem 1rem;
  }
  .step-item.is-first,
  .step-item.is-last {
    clip-path: none;
    border-radius: 8px;
  }
  .deadline-inline { display: inline; }
  .deadline-row { display: none; }
  .step-item.not-first { margin-left: 0; }
  .step-chevron { display: none; }
}

@media (min-width: 481px) {
  /* keep rounded corners for first/last on wide screens */
  .step-item.is-first {
    clip-path: polygon(
      0 0,
      calc(100% - 20px) 0,
      100% 50%,
      calc(100% - 20px) 100%,
      0 100%
    );
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }
  .step-item.is-last {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%, 20px 50%);
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }
  .step-item:not(.is-first):not(.is-last) { border-radius: 0; }
  .step-label {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    line-height: 1.2;
    max-height: calc(1em * 1.2 * 2);
  }
}
</style>
